import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Tutorial.css';

export default function Tutorial(props) {
    const {
        inline = false,
        onClose,
        mode: modeProp,
        type: typeProp,
        scenario: scenarioProp,
        initialX = window.innerWidth / 2 - 320,
        initialY = 80
    } = props;
    const navigate = useNavigate();
    const { search } = useLocation();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const mode = modeProp ?? params.get('interface');            // 'write' or 'annotate'
    const type = typeProp ?? params.get('type');                // 'editor', 'tagging', 'list', or 'annotation'
    const scenario = scenarioProp ?? params.get('scenario');   // 'co-write' or 'auto-complete'


    // Define slide content based on mode and type
    const slides = [];

    if (mode === 'write') {
        if (type === 'editor') {
            if (scenario === 'human-only') {
                slides.push({
                    title: 'Human-Only Mode: Write Your Poem',
                    content: (
                        <>
                            <p>
                                Welcome to <strong>Human-Only</strong> mode. In this scenario, you have a
                                distraction-free editor on the left—no AI suggestions, no config panel.
                            </p>
                            <ul>
                                <li>
                                    <strong>Draft Poem</strong>: Write and edit your poem directly here.
                                </li>
                                <li>
                                    No AI output section and no right-hand panel.
                                </li>
                                <li>
                                    When ready, click <strong>Publish</strong> to tag your poem.
                                </li>
                            </ul>
                        </>
                    )
                });
            } else if (scenario === 'curator') {
                // Co-write scenario tutorial slides
                slides.push({
                    title: "Co-writing Poetry with AI",
                    content: (
                        <>
                            <p>Welcome to the Co-write mode, where you and AI collaborate to create beautiful poetry together! This powerful interface allows you to generate AI-suggested poetry based on your preferences, select the outputs you like, and incorporate them into your draft.</p>
                            <p>The Co-write interface consists of three main sections:</p>
                            <ul>
                                <li><strong>Draft Poem (Left-Top)</strong>: This is where you write and edit your poem. You can start from scratch or build upon AI suggestions.</li>
                                <li><strong>Model Outputs (Left-Bottom)</strong>: This area displays AI-generated poetry suggestions based on your configuration settings. You can select one or more suggestions to add to your draft.</li>
                                <li><strong>Command Configuration (Right)</strong>: This panel allows you to customize how the AI generates poetry, including creativity level, poetic elements, rhyme scheme, and more.</li>
                            </ul>
                            <p>To switch between Co-write and Auto-complete modes, use the buttons at the top of the interface. The Co-write mode offers more control over the generation process, allowing you to select specific outputs and configure detailed parameters.</p>
                        </>
                    ),
                });
                slides.push({
                    title: "Setting Up Your Poetry Generation",
                    content: (
                        <>
                            <p>The Command Configuration panel on the right side of your screen is where you customize how the AI generates poetry. Each setting influences the style, structure, and content of the AI's suggestions.</p>
                            <p><strong>Creativity Level</strong>:</p>
                            <p>This setting controls how creative or conventional the AI's suggestions will be.</p>
                            <ul>
                                <li><strong>Low</strong>: Produces more predictable, conventional poetry</li>
                                <li><strong>Medium</strong>: Balances creativity with coherence</li>
                                <li><strong>High</strong>: Generates more unique, unexpected, and experimental poetry</li>
                            </ul>
                            <p><strong>Poetic Element</strong>:</p>
                            <p>Select a specific literary device for the AI to incorporate:</p>
                            <ul>
                                <li><strong>None</strong>: No specific poetic element required</li>
                                <li><strong>Simile</strong>: Compares two things using "like" or "as"</li>
                                <li><strong>Metaphor</strong>: Makes a comparison without using "like" or "as"</li>
                                <li><strong>Personification</strong>: Gives human qualities to non-human objects</li>
                                <li><strong>Hyperbole</strong>: Uses extreme exaggeration</li>
                                <li><strong>Irony</strong>: Expresses meaning opposite to the literal meaning</li>
                                <li><strong>Alliteration</strong>: Repeats consonant sounds at the beginning of words</li>
                                <li><strong>Onomatopoeia</strong>: Uses words that imitate sounds</li>
                                <li><strong>Oxymoron</strong>: Combines contradictory terms</li>
                                <li><strong>Paradox</strong>: Contains a statement that contradicts itself but might be true</li>
                            </ul>
                        </>
                    ),
                });
                slides.push({
                    title: "Setting Up Your Poetry Generation (Continued)",
                    content: (
                        <>
                            <p><strong>Rhyme Schema</strong>:</p>
                            <p>Choose a rhyming pattern for your poem:</p>
                            <ul>
                                <li><strong>None</strong>: No specific rhyme pattern</li>
                                <li><strong>Free verse</strong>: Poetry without regular patterns of rhyme</li>
                                <li><strong>AAAA</strong>: All lines rhyme with each other</li>
                                <li><strong>AABB</strong>: First two lines rhyme, last two lines rhyme</li>
                                <li><strong>ABAB</strong>: First and third lines rhyme, second and fourth lines rhyme</li>
                                <li><strong>ABBA</strong>: First and last lines rhyme, middle two lines rhyme</li>
                                <li><strong>Haiku</strong>: Traditional Japanese form with 5-7-5 syllable structure</li>
                            </ul>
                            <p><strong>Source of Inspiration</strong>:</p>
                            <p>Enter a word, phrase, or theme to inspire the AI. This helps focus the content of the generated poetry.</p>
                            <p><strong>Number of Responses</strong>:</p>
                            <p>Use the slider to select how many different poem variations the AI should generate (1-4). More responses give you more options to choose from.</p>
                            <p><strong>Line Limit</strong>:</p>
                            <p>Set how many lines each generated poem should contain (2-12). This controls the length of each AI suggestion.</p>
                        </>
                    ),
                });
                slides.push({
                    title: "Generating and Selecting AI Suggestions",
                    content: (
                        <>
                            <p>Once you've configured your preferences, click the "Generate" button to create AI poetry suggestions. The system will process your request, and you'll see "Generating poetry..." while it works.</p>
                            <p>When generation is complete, the Model Outputs section will display the AI's suggestions based on your configuration settings. Each suggestion is a complete poem that follows your specified parameters.</p>
                            <p><strong>Selecting Outputs</strong>:</p>
                            <ul>
                                <li>Each output has a circle to its left that you can click to select it</li>
                                <li>The circle fills in when selected, indicating that output is chosen</li>
                                <li>You can select multiple outputs if you want to combine several suggestions</li>
                                <li>Click a selected circle again to deselect it</li>
                            </ul>
                            <p><strong>Adding to Your Draft</strong>:</p>
                            <p>After selecting one or more outputs, click the "Add to Draft" button at the bottom of the Model Outputs section. This will add your selected poetry to your draft in the editor above.</p>
                            <p>The selected outputs will be added to the end of your current draft, separated by blank lines. You can then edit and refine this content in the Draft Poem section.</p>
                        </>
                    ),
                });
                slides.push({
                    title: "Refining Your Poetry",
                    content: (
                        <>
                            <p>The Draft Poem section is where you write, edit, and refine your poetry. After adding AI
                                suggestions to your draft, you'll likely want to make adjustments to create a cohesive
                                final poem.</p>
                            <p><strong>Editor Toolbar</strong>:</p>
                            <p>The toolbar above the editor provides several useful functions:</p>
                            <ul>
                                <li><strong>Undo</strong>: Reverts your last change</li>
                                <li><strong>Redo</strong>: Reapplies a change you've undone</li>
                                <li><strong>A-</strong>: Decreases the font size for better readability</li>
                                <li><strong>A+</strong>: Increases the font size for better readability</li>
                            </ul>
                            <p><strong>Editing Techniques and Suggestions</strong>:</p>
                            <ul>
                                <li><strong>Combine and Rearrange</strong>: Move lines around to create better flow</li>
                                <li><strong>Refine Language</strong>: Adjust wording to better match your voice</li>
                                <li><strong>Maintain Consistency</strong>: Ensure theme and style remain consistent</li>
                                <li><strong>Add Personal Touches</strong>: Insert your own lines to complement AI
                                    suggestions
                                </li>
                                <li><strong>Remove Redundancy</strong>: Delete repetitive or unnecessary content</li>
                            </ul>
                            <p>When you're satisfied with your poem, it's time to publish it. This process allows you to
                                tag your poem with metadata and save it to the system.</p>
                            <p><strong>Publishing Process</strong>:</p>
                            <ol>
                                <li>Click the "Publish" button at the bottom of the Draft Poem section</li>
                                <li>You'll be taken to the Tagging Page where you can add metadata to your poem</li>
                                <li>On the Tagging Page, you'll see your final poem displayed on the left</li>
                            </ol>
                        </>
                    ),
                });
            } else if (scenario === 'co-write') {
                // Auto-complete scenario tutorial slides
                slides.push({
                    title: "Using AI Auto-completion for Poetry",
                    content: (
                        <>
                            <p>Welcome to the Auto-complete mode, a streamlined approach to poetry creation where the AI continues your writing in real-time! Unlike Co-write mode, which generates complete poems based on your configuration, Auto-complete mode extends your existing text line by line, helping you develop your poem organically.</p>
                            <p>The Auto-complete interface has a simplified layout:</p>
                            <ul>
                                <li><strong>Draft Poem (Left)</strong>: This is where you write your initial lines and see the AI's continuations added directly to your text.</li>
                                <li><strong>Auto-complete Panel (Right)</strong>: A compact panel with just two essential settings: creativity level and number of lines to complete.</li>
                            </ul>
                            <p>Auto-complete mode is perfect for when you have a strong starting idea but want assistance developing it further. It's also ideal for overcoming writer's block or exploring new directions for your poetry.</p>
                            <p>To switch between Auto-complete and Co-write modes, use the buttons at the top of the interface. You can switch modes at any time, though be aware that changing modes will clear your current draft.</p>
                        </>
                    ),
                });
                slides.push({
                    title: "Beginning Your Poem",
                    content: (
                        <>
                            <p>In Auto-complete mode, how you start your poem significantly influences the AI's continuations. The initial lines you write establish the tone, style, and theme that the AI will attempt to maintain.</p>
                            <p><strong>Tips for Starting Your Poem</strong>:</p>
                            <ol>
                                <li><strong>Set a Clear Tone</strong>: Begin with lines that establish the emotional
                                    tone you want—whether melancholic, joyful, contemplative, or something
                                    else.<br/><br/></li>
                                <li><strong>Establish a Theme</strong>: Introduce your main subject or theme early to
                                    guide the AI's understanding of your poem's focus.<br/><br/></li>
                                <li><strong>Consider Form</strong>: If you want a specific structure (like rhyming
                                    couplets), establish this pattern in your first few lines.<br/><br/></li>
                                <li><strong>Use Strong Imagery</strong>: Vivid imagery in your opening lines helps the
                                    AI generate similarly rich continuations.<br/><br/></li>
                                <li><strong>Be Specific</strong>: The more specific your initial content, the more
                                    focused the AI's continuations will be.<br/><br/></li>
                            </ol>
                            <p>To begin writing, simply click in the Draft Poem editor and start typing. There's no minimum requirement—you could start with just a single line—but providing 2-4 lines generally gives the AI more context to work with.</p>
                        </>
                    ),
                });
                slides.push({
                    title: "Setting Auto-completion Parameters",
                    content: (
                        <>
                            <p>The Auto-complete panel on the right side of your screen offers two simple but powerful settings to control how the AI continues your poem:</p>
                            <p><strong>Creativity Level</strong>:</p>
                            <p>This setting determines how conventional or experimental the AI's continuations will be:</p>
                            <ul>
                                <li><strong>Low</strong>: Produces more predictable, conservative continuations that closely follow established patterns in your writing. This setting prioritizes coherence and consistency.</li>
                                <li><strong>Medium</strong>: Balances creativity with coherence, offering continuations that extend your ideas while occasionally introducing new elements. This is the default setting and works well for most poetry.</li>
                                <li><strong>High</strong>: Generates more surprising, unique, and experimental continuations. This setting might introduce unexpected imagery or take your poem in new directions while still maintaining connection to your theme.</li>
                            </ul>
                            <p><strong>Lines to Complete</strong>:</p>
                            <p>This setting controls how many lines the AI will add each time you click the Auto-complete button:</p>
                            <ul>
                                <li>You can select between 1-4 lines per completion</li>
                                <li>For more control, choose 1 line at a time</li>
                                <li>For faster development, choose multiple lines</li>
                                <li>Consider the natural structure of your poem when deciding (e.g., completing couplets with 2 lines)</li>
                            </ul>
                        </>
                    ),
                });
                slides.push({
                    title: "Generating Continuations",
                    content: (
                        <>
                            <p>Once you've written your initial lines and set your parameters, you're ready to use the AI to continue your poem:</p>
                            <ol>
                                <li>Click the "Auto-complete" button in the right panel</li>
                                <li>The system will process your request (you'll see "Completing..." while it works)</li>
                                <li>When complete, the new lines will be automatically added to your draft</li>
                            </ol>
                            <p>Unlike Co-write mode, there's no selection process—the generated lines are added directly to your poem. This creates a seamless, flowing writing experience.</p>
                            <p><strong>Effective Auto-completion Strategies</strong>:</p>
                            <ul>
                                <li><strong>Iterative Development</strong>: Generate a few lines, review and edit them, then generate more. This gives you more control over the poem's direction.</li>
                                <li><strong>Guiding the AI</strong>: If you want to steer the poem in a specific direction, add a line of your own before clicking Auto-complete again.</li>
                                <li><strong>Handling Unexpected Continuations</strong>: If you don't like the generated lines, you can:
                                    <ul>
                                        <li>Delete them and try again (possibly with different settings)</li>
                                        <li>Edit them to better match your vision</li>
                                        <li>Keep parts you like and modify the rest</li>
                                    </ul>
                                </li>
                            </ul>
                        </>
                    ),
                });
                slides.push({
                    title: "The Iterative Writing Process",
                    content: (
                        <>
                            <p>Creating a complete poem with Auto-complete is an iterative process that combines your
                                writing with AI assistance. Here's how to build a cohesive poem through multiple
                                auto-completions:</p>
                            <p><strong>Multi-stage Development Process</strong>:</p>
                            <ol>
                                <li><strong>Write Initial Lines</strong>: Begin with your own lines to establish theme
                                    and style<br/><br/>
                                </li>
                                <li><strong>Generate First Continuation</strong>: Add 1-4 lines using Auto-complete<br/><br/>
                                </li>
                                <li><strong>Review and Edit</strong>: Refine the generated content as needed<br/><br/>
                                </li>
                                <li><strong>Add Your Own Lines</strong>: Optionally insert your own lines to guide
                                    direction<br/><br/>
                                </li>
                                <li><strong>Generate Next Continuation</strong>: Request more lines from the
                                    AI<br/><br/></li>
                                <li><strong>Repeat Steps 3-5</strong>: Continue this cycle until your poem feels
                                    complete<br/><br/>
                                </li>
                            </ol>
                            <p><strong>Maintaining Thematic Consistency</strong>:</p>
                            <ul>
                                <li>Periodically read your entire poem to ensure it maintains a coherent theme</li>
                                <li>If the poem begins to drift, add a strong thematic line of your own before the next
                                    auto-completion
                                </li>
                                <li>Edit generated lines to strengthen thematic connections</li>
                            </ul>
                            <p>When you're satisfied with your auto-completed poem, it's time to publish it. This
                                process allows you to tag your poem with metadata and save it to the system.</p>
                            <p><strong>Publishing Process</strong>:</p>
                            <ol>
                                <li>Click the "Publish" button at the bottom of the Draft Poem section</li>
                                <li>You'll be taken to the Tagging Page where you can add metadata to your poem</li>
                                <li>On the Tagging Page, you'll see your final poem displayed on the left</li>
                            </ol>
                        </>
                    ),
                });
            } else if (scenario === 'ai-only') {
                slides.push({
                    title: 'AI-Only Mode: Interface Overview',
                    content: (
                        <>
                            <p>
                                In <strong>AI-Only</strong> mode there is no draft editor—everything happens
                                via the left-hand Model Outputs and the right-hand Command Configuration panel. Once
                                you were satisfied with any suggestions of AI which you think is expressive of a feeling
                                or emotion you desired, you can easily select it and click "Publish" to go to the next page.
                            </p>
                            <ul>
                                <li>
                                    <strong>Model Outputs (Left)</strong>: After each generate request you’ll
                                    see up to four AI-suggested poems.
                                </li>
                                <li>
                                    <strong>Command Configuration (Right)</strong>: Exactly the same full set of
                                    controls as Curator mode:
                                    <ul>
                                        <li><strong>Creativity Level</strong> (Low, Medium, High)</li>
                                        <li><strong>Poetic Element</strong> (None, Simile, Metaphor, …)</li>
                                        <li><strong>Rhyme Schema</strong> (None, Free verse, AABB, ABAB, ABBA, Haiku)</li>
                                        <li><strong>Source of Inspiration</strong> (a guiding word or phrase)</li>
                                        <li><strong>Number of Responses</strong> (1–4 variations)</li>
                                        <li><strong>Line Limit</strong> (2–12 lines per suggestion)</li>
                                    </ul>
                                </li>
                            </ul>
                            <p>
                                Configure your settings, then click <strong>Generate</strong> to pull fresh AI-only suggestions.
                            </p>
                        </>
                    ),
                });
                slides.push({
                    title: 'Selecting & Publishing Your Poem',
                    content: (
                        <>
                            <p>
                                Once up to four poems are generated (each up to twelve lines):
                            </p>
                            <ul>
                                <li>Click the circle next to the single poem you wish to keep.</li>
                                <li>The circle will fill in to confirm your selection.</li>
                                <li>Click <strong>Publish</strong> to tag and save that one poem.</li>
                            </ul>
                        </>
                    ),
                });

            }else {
                // Default editor tutorial (keep your existing slides here)
                slides.push({
                    title: 'Writing Your Poem',
                    content: (
                        <>
                            <p>Welcome to the Poetry Editor! Here's how to begin:</p>
                            <ul>
                                <li>Use the <strong>Command Configuration</strong> panel on the right to set creativity, poetic devices, rhyme scheme, etc.</li>
                                <li>Click <strong>Generate</strong> to get AI suggestions.</li>
                                <li>Select outputs by clicking the circles, then <strong>Add to Draft</strong> to incorporate them into your poem.</li>
                                <li>Use <strong>Undo/Redo</strong> and font controls for fine-tuning.</li>
                                <li>When you're happy, click <strong>Publish</strong> to tag your poem.</li>
                            </ul>
                        </>
                    ),
                });
                slides.push({
                    title: 'Self‑Reporting Expressiveness',
                    content: (
                        <>
                            <p>After publishing, you'll self‑report the following features:</p>
                            <ul>
                                <li><strong>Mental Image:</strong> How vividly you visualize the poem.</li>
                                <li><strong>Flow:</strong> How smoothly it reads.</li>
                                <li><strong>Intended Mood:</strong> How well it matches your emotional intent.</li>
                                <li><strong>Originality:</strong> How unique and creative it feels.</li>
                            </ul>
                        </>
                    ),
                });
            }
        }
        if (type === 'tagging') {
            slides.push({
                title: "Tagging Your Poem",
                content: (
                    <>
                        <p>In the left panel you can see your final poem, still you can return to the editor and refine
                        your poem, in the right side is the tagging form which should be completed as a self-report.</p>
                        <p><strong>Tagging Your Poem</strong>:</p>
                        <p>On the Tagging form, you'll need to:</p>
                        <ul>
                            <li>Rate your poem on four expressiveness metrics (1-5 scale):
                                <ul>
                                    <li><strong>Mental Image</strong>: Mental imagery points to the effect generated by
                                        a poem for the reader and efficient understanding of poetry.
                                        Mental imagery enables the readers to experience the scene described in the poem
                                        through their mind.
                                    </li>
                                    <li><strong>Flow</strong>: Considered to assess how smoothly and naturally the
                                        poem’s rhyme and rhythm are recognized when one's read it aloud.
                                    </li>
                                    <li><strong>Intended Mood</strong>:This metric focuses on how well the poem conveys
                                        poet's emotional atmosphere, an atmosphere that directly corresponds to
                                        the “Theme” category chosen at this same page (e.g., Joyful, Melancholy). A
                                        higher point for this feature means that the poet's emotional goal were
                                        expressed
                                        clearly and readers could feel the intended emotion better. On the other hand,
                                        the lower point indicates that poet's feelings and intentions are injected
                                        ambiguously
                                        or overshadowed by other elements, making it difficult for readers to understand
                                        the poet's mood.
                                    </li>
                                    <li><strong>Originality</strong>: Refers to how unique and novel the poem looks in
                                        phrase usage, approach and the way of structuring the mental imagery. Using some
                                        famous phrases
                                        (e.g. previously used similes, metaphors and other forms of concepts provided
                                        through figurative languages) used by other poets and using clichés could result
                                        in less original poems.
                                    </li>
                                </ul>
                            </li>
                            <li>Select a <strong>Primary Mood</strong> that describes your whole poem's atmosphere
                                in the best way from the dropdown menu</li>
                            <li>Click "Submit & Publish" to save your poem in the database</li>
                        </ul>
                    </>
                ),
            });
        }
    } else if (mode === 'annotate') {
        if (type === 'list') {
            slides.push({
                title: 'Choosing Poems to Annotate',
                content: (
                    <>
                        <p>Welcome to the Poem List! This page displays all available poems that you can annotate.</p>
                        <p><strong>Navigating the Poem List</strong>:</p>
                        <ul>
                            <li>Each poem entry shows a preview of the poem's content</li>
                            <li>Scroll through the list to browse all available poems</li>
                            <li>Use the search or filter options if available to find specific poems</li>
                        </ul>
                        <p><strong>Selecting a Poem</strong>:</p>
                        <ul>
                            <li>Click the <strong>Annotate</strong> button next to any poem to open it in the annotation
                                interface
                            </li>
                            <li>You can annotate as many poems as you like</li>
                            <li>Previously annotated poems may be marked differently (if this feature is implemented)
                            </li>
                        </ul>
                        <p>The poem list provides an overview of all content available for annotation. Take your time to
                            select poems that interest you for providing feedback.</p>
                    </>
                ),
            });
            // slides.push({
            //     title: 'Understanding Poem Previews',
            //     content: (
            //         <>
            //             <p>Each poem in the list contains important information to help you decide which poems to annotate:</p>
            //             <ul>
            //                 <li><strong>Poem Content Preview</strong>: The first few lines of the poem</li>
            //                 <li><strong>Poem ID</strong>: A unique identifier for each poem</li>
            //                 <li><strong>Annotation Status</strong>: Some implementations may show whether you've already annotated a poem</li>
            //             </ul>
            //             <p><strong>Best Practices for Selection</strong>:</p>
            //             <ul>
            //                 <li>Read the preview to get a sense of the poem's style and content</li>
            //                 <li>Select poems with diverse themes and styles for a broader annotation experience</li>
            //                 <li>Consider annotating poems that evoke strong reactions (positive or negative)</li>
            //             </ul>
            //             <p>Click the <strong>Annotate</strong> button when you're ready to provide feedback on a specific poem.</p>
            //         </>
            //     ),
            // });
        }
        if (type === 'annotation') {
            slides.push({
                title: 'Annotating a Poem',
                content: (
                    <>
                        <p>Welcome to the Annotation Interface! Here you can provide valuable feedback on the poem you've selected.</p>
                        <p>The annotation page is divided into two main sections:</p>
                        <ul>
                            <li><strong>Poem Display</strong>: The left side shows the complete poem text</li>
                            <li><strong>Annotation Form</strong>: The right side contains all feedback fields</li>
                        </ul>
                        <p>Take your time to read the poem thoroughly before beginning your annotation.</p>
                    </>
                ),
            });
            slides.push({
                title: 'Providing Feedback',
                content: (
                    <>
                        <p>The annotation form includes several important fields:</p>
                        <ul>
                            <li><strong>Evaluator ID</strong> (optional): Enter your identifier if requested</li>
                            <li><strong>Expressiveness Metrics</strong>: Rate the poem on four key dimensions using the sliders or rating controls</li>
                            <li><strong>Assumed Theme</strong>: Select what you believe is the main theme of the poem</li>
                        </ul>
                        <p><strong>Rating the Expressiveness Metrics</strong>:</p>
                        <ul>
                            <li><strong>Mental Image</strong>: Mental imagery points to the effect generated by a poem for the reader and efficient understanding of poetry.
                                Mental imagery enables the readers to experience the scene described in the poem through their mind.</li>
                            <li><strong>Flow</strong>: Considered to assess how smoothly and naturally the poem’s rhyme and rhythm are recognized when one's read it aloud.</li>
                            <li><strong>Intended Mood</strong>:This metric focuses on how well the poem conveys poet's emotional atmosphere, an atmosphere that directly corresponds to
                                the “Theme” category chosen at this same page (e.g., Joyful, Melancholy). A higher point for this feature means that the poet's emotional goal were expressed
                                clearly and readers could feel the intended emotion better. On the other hand, the lower point indicates that poet's feelings and intentions are injected ambiguously
                                or overshadowed by other elements, making it difficult for readers to understand the poet's mood. </li>
                            <li><strong>Originality</strong>: Refers to how unique and novel the poem looks in phrase usage, approach and the way of structuring the mental imagery. Using some famous phrases
                                (e.g. previously used similes, metaphors and other forms of concepts provided through figurative languages) used by other poets and using clichés could result in less original poems.</li>
                        </ul>
                    </>
                ),
            });
            slides.push({
                title: 'Submitting Your Annotation',
                content: (
                    <>
                        <p>Once you've completed all required fields in the annotation form:</p>
                        <ol>
                            <li>Review your ratings to ensure they accurately reflect your assessment</li>
                            <li>Check that you've selected an appropriate theme from the dropdown</li>
                            <li>Click the <strong>Submit Annotation</strong> button at the bottom of the form</li>
                        </ol>
                        <p><strong>After Submission</strong>:</p>
                        <ul>
                            <li>You'll receive confirmation that your annotation was saved</li>
                            <li>You'll be returned to the Poem List to potentially select another poem</li>
                            <li>Your feedback will be stored and associated with the poem you annotated</li>
                        </ul>
                        <p>Thank you for contributing your valuable perspective on this poem!</p>
                    </>
                ),
            });
        }
    }

    // Navigation logic
    const [current, setCurrent] = useState(0);
    const total = slides.length;
    const isFirst = current === 0;
    const isLast = current === total - 1;
    const { title, content } = slides[current];

    // Drag the window
    const [pos, setPos] = useState({ x: initialX, y: initialY });
    const dragData = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

    const onMouseDownHeader = (e) => {
        dragData.current.dragging = true;
        dragData.current.offsetX = e.clientX - pos.x;
        dragData.current.offsetY = e.clientY - pos.y;
        // Prevent text selection while dragging
        document.body.style.userSelect = 'none';
    };

    const onMouseMove = (e) => {
        if (!dragData.current.dragging) return;
        setPos({
            x: Math.max(8, Math.min(e.clientX - dragData.current.offsetX, window.innerWidth - 8 - 640)),
            y: Math.max(8, Math.min(e.clientY - dragData.current.offsetY, window.innerHeight - 8 - 80)), // keep on screen
        });
    };

    const onMouseUp = () => {
        dragData.current.dragging = false;
        document.body.style.userSelect = '';
    };

    useEffect(() => {
        if (!inline) return; // drag only matters for popup
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [inline, pos.x, pos.y]);


    const handleNext = () => {
        if (isLast) {
            if (inline) onClose?.();       // <-- close popup
            else navigate(-1);             // <-- only when full-page route
        } else {
            setCurrent((i) => i + 1);
        }
    };

    const handlePrevOrClose = () => {
        if (isFirst) {
            if (inline) onClose?.();       // <-- close popup
            else navigate(-1);
        } else {
            setCurrent((i) => i - 1);
        }
    };

    const containerClass = inline ? 'tutorial-popup' : 'tutorial-container';

    return (
        <div
            className={containerClass}
            style={
                inline
                    ? { left: pos.x, top: pos.y, position: 'fixed', width: 640, zIndex: 1000 }
                    : undefined
            }
            role="dialog"
            aria-modal={inline ? 'false' : 'true'}
        >
            <div className="tutorial-header" onMouseDown={inline ? onMouseDownHeader : undefined}>
                <h2 className="tutorial-title">{title}</h2>
                {inline && (
                    <button className="tutorial-close" onClick={() => onClose?.()} aria-label="Close tutorial">
                        ✕
                    </button>
                )}
            </div>

            <div className="tutorial-content">{content}</div>

            <div className="tutorial-nav">
                <button onClick={handlePrevOrClose}>
                    {isFirst ? 'Close' : 'Previous'}
                </button>
                <button onClick={handleNext}>{isLast ? 'Finish' : 'Next'}</button>
            </div>
        </div>
    );
}